export enum TrainingType {
  OPEN = 'Open',
  REQUESTABLE = 'Requestable',
  PRIVATE = 'Private'
}

export enum TrainingStatus {
  NOT_ENROLLED = 'Not Enrolled',
  REQUESTED = 'Requested',
  APPROVED = 'Approved',
  DENIED = 'Denied',
  NOT_STARTED = 'Not Started',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Successfully Completed',
  INCOMPLETE = 'Training Incomplete',
  ESCALATED = 'Escalated',
  FAILED = 'Failed',
  PASSED = 'Passed'
}

export interface TrainingMaster {
  id: number;
  trainingId: string;
  trainingName: string;
  active: boolean;
  startDate: string;
  endDate: string;
  lastNotifiedEndDate?: string;
  audienceEmails: string;
  stakeholderEmails: string;
  trainerEmails: string;
  trainingLink?: string;
  assessments?: string;
  estimatedEffortHours?: number;
  passingPercentage: number;
  isMandatory?: boolean;
  isTrainer?: boolean;
  isStakeholder?: boolean;
  notificationVersion?: string;
  trainingType?: TrainingType;
  programType?: 'AI' | 'TrainerLed' | 'AINested';
  ispublished?: boolean;
  createdBy?: string;
  discussionEnabled?: boolean;
}

// Participant Status (Now normalized to camelCase)
export interface ParticipantTrainingStatus {
  id: number;
  trainingId: string;
  moduleId: string;
  participantEmail: string;
  status: TrainingStatus;
  assignedDate: string;
  completedDate?: string;
  escalated?: boolean;
  escalationDate?: string;
}

// Assessment Questions (Now normalized to camelCase)
export interface AssessmentQuestion {
  id: number;
  trainingId: string;
  moduleId: string;
  question: string;
  codeSnippet?: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  answer: string;
}

// Assessment Submissions (Now normalized to camelCase)
export interface AssessmentSubmission {
  id?: number;
  trainingId: string;
  moduleId: string;
  audienceEmail: string;
  rawScore: number;
  totalPoints: number;
  resultStatus: TrainingStatus.PASSED | TrainingStatus.FAILED;
}

// UI Models (derived from SharePoint data)
export interface Training {
  id: number;
  trainingId: string;
  trainingName: string;
  status: TrainingStatus;
  assignedDate: string;
  completedDate?: string;
  trainingLink?: string;
  estimatedEffortHours?: number;
  passingPercentage: number;
  active: boolean;
  startDate: string;
  endDate: string;
  stakeholderEmails: string;
  audienceEmails?: string;
  percentage?: number;
  escalationDate?: string;
  trainingType?: TrainingType;
  programType?: 'AI' | 'TrainerLed' | 'AINested';
  catalogVisible?: boolean;
  isMandatory?: boolean;
  isTrainer?: boolean;
  isStakeholder?: boolean;
  courseId?: number;
  discussionEnabled?: boolean;
  assessments?: string; // JSON string of NestedModule[] for TrainerLed programs
  createdBy?: string;
}

export interface Module {
  moduleId: string;
  trainingId: string;
  name: string;
  questionCount: number;
  status?: TrainingStatus.NOT_STARTED | TrainingStatus.PASSED | TrainingStatus.FAILED | TrainingStatus.IN_PROGRESS | TrainingStatus.INCOMPLETE;
  estimatedTime?: number;
  timelimit?: number;
  description?: string;
  answer?: string;
  deadline?: string;
  submissionid?: number;
  introfiles?: { id: number, filename: string, bloburl: string, filesize: number, userid?: number }[];
  submissionfiles?: { id: number, filename: string, bloburl: string, filesize: number, userid?: number }[];
  maxCredits?: number;
  achievedCredits?: number;
  stateDeadlines?: Record<string, number>;
  workflowEnabled?: boolean;
  currentState?: string;
  stateHistory?: Array<{ state: string; ts: number; by: string; note?: string }>;
  trainerEval?: string;
  timeSubmitted?: number;
  // Trainer-led "Under review": who started reviewing, and when (0 = no review)
  reviewBy?: string;
  reviewStartedAt?: number;
  evalCriteria?: { name: string }[];
  extractedQuestions?: string;
  // AI-led fields
  attempts?: number;
  attemptLimit?: number;
  grade?: number;
  moduleType?: 'AI' | 'Manual';
  moodleInstance?: number;
  cmid?: number;
  lastAttempt?: string | null;
}

// AI-led nested hierarchy types
export interface AINestedModule {
  id: string;
  moodleId?: number;
  name: string;
  originalName?: string;
  description: string;
  difficulty: string;
  questionCount: number;
  estimatedTime: number;
  attemptLimit: number;
  deadline?: number;
  questions: { id: string; question: string; answer: string; codeSnippet: string; language?: string }[];
  generated: boolean;
  generating: boolean;
  extraPrompt?: string;
}

export interface AINestedChapter {
  id: string;
  name: string;
  modules: AINestedModule[];
}

export interface AINestedSubject {
  id: string;
  name: string;
  chapters: AINestedChapter[];
}

export interface Question {
  id: number;
  trainingId: string;
  moduleId: string;
  question: string;
  codeSnippet?: string;
  requiredLanguage?: string;
  options: {
    A: string;
    B: string;
    C: string;
    D: string;
  };
  correctAnswer?: string; // Optional - not sent to client for security
  graderInfo?: string; // Reference answer for AI grading
}

export interface AssessmentData {
  training: Training;
  module: Module;
  questions: Question[];
}

export interface UserAnswer {
  questionId: number;
  selectedAnswer: string;
  isCorrect: boolean;
}

export interface AssessmentResult {
  trainingId: string;
  moduleId: string;
  rawScore: number;
  totalPoints: number;
  percentage: number;
  resultStatus: TrainingStatus.PASSED | TrainingStatus.FAILED;
  submittedDate: string;
}

export interface LeaderboardEntry {
  email: string;
  fullname: string;
  totalscore: number;
  attempts?: number;          // trainer-led: graded assignment count
  maxscore?: number;          // trainer-led: total possible points
  modulescompleted?: number;  // ai-led: modules with a grade
  totalmodules?: number;      // ai-led: total modules in program
}

export interface UngradedParticipant {
  email: string;
  fullname: string;
  submitted: number;  // assignments with a submission (status 'Submitted' or 'Passed')
  total: number;       // total assignments in the program
}

export type BulkEvaluationRowStatus = 'no-submission' | 'eligible' | 'done';

export interface BulkEvaluationRow {
  userid: number;
  email: string;
  fullname: string;
  status: BulkEvaluationRowStatus;
}

export interface NestedAssignment {
  id: string; // React temp UUID
  moodleId?: number; // Native Moodle assignment ID
  title: string;
  description: string;
  deadline: any; // dayjs / ISO string
  maxCredits: number;
  achievedCredits?: number;
  stateDeadlines?: Record<string, number | null>; // per-state target deadlines
  workflowEnabled?: boolean;
  evalCriteria?: { name: string }[];
  status?: string;
  submissionid?: number;
  introfiles?: any[];
  submissionfiles?: any[];
}

export interface NestedSubModule {
  id: string; // React temp UUID
  name: string;
  assignments: NestedAssignment[];
}

export interface NestedModule {
  id: string; // React temp UUID
  name: string;
  subModules?: NestedSubModule[];
  assignments?: NestedAssignment[];
  structureType?: 'structured' | 'direct' | 'mixed';
}